//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { IHttpService, IPromise, IWindowService } from 'angular';
import { IPromiseService } from 'pinsights-js-shared';

import GaProfileId from '@models/GaProfileId';
import { SessionVarsService } from '@services/session-vars-service';

const curalate = angular.module('curalate');

const BASE_URL_PIXEL = '/api/settings/pixel';
const BASE_URL_GA = '/api/settings/ga';
const BASE_URL_ADOBE = '/api/settings/adobe';
const BASE_URL_GOOGLE_GATEWAY = '/api/v1/google/analytics';
export interface ITrackingPixelDto {
  pixelId: number;
  isActive: boolean;
}

export interface IGaProfile {
  // TODO: Is one of these a GaProfileId?
  id: string;
  account_id: string;
  web_property_id: string;
  name: string;
  label: string;
  timezone: string;
  currency: string;
  ecommerce_tracking?: boolean;
  enhanced_ecommerce_tracking?: boolean;
  ecommerce_enabled: boolean;
}

export interface IGASettingsInfoDto {
  currentProfileId?: string;
  profileOptions: IGaProfile[];
}

export interface IAdobeSettingsInfoDto {
  isConnected: boolean;
  environmentOptions: {
    value: string;
    name: string;
  }[];
}

export interface IOmnitureCredentials {
  username: string;
  sharedSecret: string;
  environment: string;
  suiteReportId: string;
}

export class AnalyticsSettingsService {
  private readonly isAnalyticsConfigMigrationEnabled: boolean;
  private readonly analyticsConfigGatewayBaseUrl: string;

  constructor(
    private readonly $http: IHttpService,
    private readonly $window: IWindowService,
    private readonly PromiseService: IPromiseService,
    sessionVarsService: SessionVarsService,
  ) {
    // TODO: PD-319318 - Remove AnalyticsConfigurationMigration feature flag
    this.isAnalyticsConfigMigrationEnabled =
      sessionVarsService.isFeatureEnabled(
        FeatureNames.AnalyticsConfigurationMigration,
      );
    this.analyticsConfigGatewayBaseUrl = sessionVarsService.getBaseUrl(
      'web_analytics_configuration_gateway',
    );
  }

  // Curalate Pixel
  public getPixel(): IPromise<ITrackingPixelDto> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(BASE_URL_PIXEL),
    );
  }

  // Google Analytics
  public getGASettings(): IPromise<IGASettingsInfoDto> {
    if (this.isAnalyticsConfigMigrationEnabled) {
      return this.PromiseService.processHttpResponse(
        this.$http.get(
          `${this.analyticsConfigGatewayBaseUrl}${BASE_URL_GOOGLE_GATEWAY}`,
        ),
      );
    }
    return this.PromiseService.processHttpResponse(this.$http.get(BASE_URL_GA));
  }

  public getGAOAuthURL(): IPromise<{ url?: string }> {
    if (this.isAnalyticsConfigMigrationEnabled) {
      return this.PromiseService.processHttpResponse(
        this.$http.post(
          `${this.analyticsConfigGatewayBaseUrl}${BASE_URL_GOOGLE_GATEWAY}/url`,
          {
            redirectUrl: `${this.$window.location.origin}/google/oauth`,
          },
        ),
      );
    }
    return this.PromiseService.processHttpResponse(
      this.$http.get(`${BASE_URL_GA}/oauth`),
    );
  }

  public deauthGAAccount(): IPromise<void> {
    if (this.isAnalyticsConfigMigrationEnabled) {
      return this.PromiseService.processHttpResponse(
        this.$http.delete(
          `${this.analyticsConfigGatewayBaseUrl}${BASE_URL_GOOGLE_GATEWAY}`,
        ),
      );
    }
    return this.PromiseService.processHttpResponse(
      this.$http.delete(`${BASE_URL_GA}/deauth`),
    );
  }

  public updateGAProfile(profileId: GaProfileId): IPromise<void> {
    if (this.isAnalyticsConfigMigrationEnabled) {
      return this.PromiseService.processHttpResponse(
        this.$http.put(
          `${this.analyticsConfigGatewayBaseUrl}${BASE_URL_GOOGLE_GATEWAY}/profile/${profileId}`,
          undefined,
        ),
      );
    }
    return this.PromiseService.processHttpResponse(
      this.$http.put(`${BASE_URL_GA}/profile/${profileId}`, undefined),
    );
  }

  // Adobe/Omniture
  public getOmnitureSettings(): IPromise<IAdobeSettingsInfoDto> {
    return this.PromiseService.processHttpResponse(
      this.$http.get(BASE_URL_ADOBE),
    );
  }

  public connectOmniture(credentials: IOmnitureCredentials): IPromise<void> {
    return this.PromiseService.processHttpResponse(
      this.$http.post(`${BASE_URL_ADOBE}/account`, credentials),
    );
  }

  public disconnectOmniture(): IPromise<void> {
    return this.PromiseService.processHttpResponse(
      this.$http.delete(`${BASE_URL_ADOBE}/account`),
    );
  }
}
curalate.service('AnalyticsSettingsService', [
  '$http',
  '$window',
  'PromiseService',
  'SessionVarsService',
  AnalyticsSettingsService,
]);
